import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import cx from "ui/cx";
import styles from "./ResizableContainer.module.scss";

type ResizeSide = "left" | "right" | "top" | "bottom";

export interface ResizableContainerProps {
  readonly children: ReactNode;
  /**
   * Which edge of the container has the drag handle.
   * - "left": container docks against right side of its parent, drag from left edge
   * - "right": container docks against left side of its parent, drag from right edge
   * - "top": container docks at bottom, drag from top edge
   * - "bottom": container docks at top, drag from bottom edge
   *
   * Defaults to "left" (right-docked panel pattern).
   */
  readonly side?: ResizeSide;
  /** Initial size in px along the resize axis. */
  readonly defaultSize?: number;
  /** Lower bound in px. The drag handle stops at this size. */
  readonly minSize?: number;
  /** Upper bound in px. The drag handle stops at this size. */
  readonly maxSize?: number;
  /**
   * Optional localStorage key. If set, the most recent size is persisted
   * across reloads. Persistence is per-domain; pick a key namespaced by
   * surface (e.g. "studio:agent-panel-width").
   */
  readonly storageKey?: string;
  /**
   * Per-side gutters in px applied as MARGIN on the container. Margin
   * (not padding) so the resize handle's outdent can reach into the gap
   * — the visible band ends up centered in the margin space between the
   * container and its sibling. Defaults to 0 on every side.
   */
  readonly gutter?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  /**
   * When true, dragging past `minSize` by `collapseThreshold` (default 60px)
   * fires `onCollapse` and ends the drag. The consumer typically responds
   * by hiding the panel from its parent layout (e.g. flipping a Redux flag).
   *
   * Hysteresis prevents accidental collapse when a user just drags to the
   * minimum size: only a deliberate over-drag past the threshold triggers.
   */
  readonly collapsible?: boolean;
  /**
   * Pixels past `minSize` the user must drag before `onCollapse` fires.
   * Defaults to 60.
   */
  readonly collapseThreshold?: number;
  /** Fired once per drag if the user crosses the collapse threshold. */
  readonly onCollapse?: () => void;
  readonly className?: string;
}

const DEFAULT_SIZE = 400;
const DEFAULT_MIN = 280;
const DEFAULT_MAX = 800;
const DEFAULT_COLLAPSE_THRESHOLD = 60;
const KEYBOARD_RESIZE_STEP = 16;

// Single-edge resizable container. The user drags one edge to grow or shrink
// the container along the corresponding axis. Cross-product primitive — no
// host-app dependencies. Surfaces (Studio, Showroom, Config, Procure) host
// their agent panel inside one of these to get a user-resizable width.
//
// Architecturally distinct from `SplitFlex`, which is a two-pane splitter
// with a draggable separator BETWEEN children. `ResizableContainer` is a
// one-pane container whose outer edge IS the handle.
//
// Persistence model: opt-in via storageKey. Reads on mount, writes after
// each drag-end (not during drag — avoid spamming localStorage during the
// mousemove).
export default function ResizableContainer({
  children,
  side = "left",
  defaultSize = DEFAULT_SIZE,
  minSize = DEFAULT_MIN,
  maxSize = DEFAULT_MAX,
  storageKey,
  gutter,
  collapsible = false,
  collapseThreshold = DEFAULT_COLLAPSE_THRESHOLD,
  onCollapse,
  className,
}: ResizableContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<number>(() => {
    if (storageKey && typeof window !== "undefined") {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= minSize && parsed <= maxSize) {
          return parsed;
        }
      }
    }
    return defaultSize;
  });
  const [isDragging, setIsDragging] = useState(false);

  const axis = side === "left" || side === "right" ? "x" : "y";

  // Persist size after drag-end (not during) so localStorage isn't hammered.
  useEffect(() => {
    if (!storageKey || isDragging) {
      return;
    }
    window.localStorage.setItem(storageKey, String(size));
  }, [storageKey, size, isDragging]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);
      const startPos = axis === "x" ? e.clientX : e.clientY;
      const startSize = size;
      // Direction multiplier: dragging the left edge LEFT increases width
      // (so delta is negated); dragging the right edge LEFT decreases width.
      const sign = side === "left" || side === "top" ? -1 : 1;

      const cleanup = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      const handleMouseMove = (ev: MouseEvent) => {
        const pos = axis === "x" ? ev.clientX : ev.clientY;
        const delta = (pos - startPos) * sign;
        const target = startSize + delta;
        // Collapse path: user has dragged past minSize by enough to commit
        // to hiding the panel. Fire onCollapse, end the drag, don't write
        // a sub-minimum size back to state (avoids a flash of clipped layout
        // before the consumer unmounts us).
        if (collapsible && onCollapse && target < minSize - collapseThreshold) {
          setIsDragging(false);
          cleanup();
          onCollapse();
          return;
        }
        const next = Math.max(minSize, Math.min(maxSize, target));
        setSize(next);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        cleanup();
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = axis === "x" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
    },
    [size, axis, side, minSize, maxSize, collapsible, collapseThreshold, onCollapse],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Home") {
        e.preventDefault();
        setSize(minSize);
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        setSize(maxSize);
        return;
      }
      const delta = getArrowResizeDelta(e.key, side);
      if (delta === undefined) {
        return;
      }
      e.preventDefault();
      setSize((currentSize) => Math.max(minSize, Math.min(maxSize, currentSize + delta)));
    },
    [side, minSize, maxSize],
  );

  // Gutters use a mixed box-model strategy:
  //   - The side WITH the handle (`side` prop) uses MARGIN. Margin creates
  //     real outside-the-box space that the handle's outdent can reach into;
  //     the visible resize band lands centered in that gap.
  //   - Other three sides use PADDING. That shrinks the content area without
  //     creating margin that wouldn't push anything in a flex parent.
  // Net result: bottom/top/non-handle-side gutters give the panel a visible
  // gap from its container's edges; handle-side gutter gives the gap that
  // the drag affordance lives in.
  const handleOffset = -((gutter?.[side] ?? 0) / 2);
  const sizeStyle: CSSProperties = axis === "x" ? { width: size } : { height: size };
  const isHandle = (s: "top" | "right" | "bottom" | "left") => s === side;
  const style: CSSProperties & Record<"--resize-handle-offset", string> = {
    ...sizeStyle,
    marginTop: isHandle("top") ? gutter?.top : undefined,
    marginRight: isHandle("right") ? gutter?.right : undefined,
    marginBottom: isHandle("bottom") ? gutter?.bottom : undefined,
    marginLeft: isHandle("left") ? gutter?.left : undefined,
    paddingTop: isHandle("top") ? undefined : gutter?.top,
    paddingRight: isHandle("right") ? undefined : gutter?.right,
    paddingBottom: isHandle("bottom") ? undefined : gutter?.bottom,
    paddingLeft: isHandle("left") ? undefined : gutter?.left,
    boxSizing: "border-box",
    // CSS custom properties are kebab-case at the DOM level — eslint's
    // naming-convention rule doesn't model that.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    "--resize-handle-offset": `${handleOffset}px`,
  };

  return (
    <div ref={containerRef} className={cx(styles.container, styles[`side-${side}`], className)} style={style}>
      <div
        className={cx(styles.handle, styles[`handle-${side}`], isDragging && styles.dragging)}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        role="separator"
        aria-orientation={axis === "x" ? "vertical" : "horizontal"}
        aria-label="Resize panel"
        aria-valuemin={minSize}
        aria-valuemax={maxSize}
        aria-valuenow={size}
        tabIndex={0}
      />
      {children}
    </div>
  );
}

function getArrowResizeDelta(key: string, side: ResizeSide): number | undefined {
  if (side === "left") {
    if (key === "ArrowLeft") {
      return KEYBOARD_RESIZE_STEP;
    }
    if (key === "ArrowRight") {
      return -KEYBOARD_RESIZE_STEP;
    }
  }
  if (side === "right") {
    if (key === "ArrowRight") {
      return KEYBOARD_RESIZE_STEP;
    }
    if (key === "ArrowLeft") {
      return -KEYBOARD_RESIZE_STEP;
    }
  }
  if (side === "top") {
    if (key === "ArrowUp") {
      return KEYBOARD_RESIZE_STEP;
    }
    if (key === "ArrowDown") {
      return -KEYBOARD_RESIZE_STEP;
    }
  }
  if (side === "bottom") {
    if (key === "ArrowDown") {
      return KEYBOARD_RESIZE_STEP;
    }
    if (key === "ArrowUp") {
      return -KEYBOARD_RESIZE_STEP;
    }
  }
  return undefined;
}
